/**
 * Safe URL Helpers
 *
 * Normalizes loose media/link values before browser rendering or storage
 * signing.
 *
 * Primary responsibilities:
 * - Extract URL strings from object-shaped payloads
 * - Reject stringified objects and route-looking junk
 * - Rewrite legacy/unreachable Supabase storage hosts to the live public origin
 * - Keep image renderers from issuing invalid browser requests
 *
 * Dependencies:
 * - None
 */

/** Live storage origin (self-hosted). Direct *.supabase.co hosts return 402. */
const DEFAULT_SUPABASE_STORAGE_PUBLIC_ORIGIN = 'https://pipeline.losi.one'

const STORAGE_OBJECT_PATH = '/storage/v1/object/'

const URL_LIKE_KEYS = [
  'url',
  'src',
  'href',
  'path',
  'uri',
  'source',
  'imageUrl',
  'image_url',
  'avatarUrl',
  'avatar_url',
  'publicUrl',
  'public_url',
  'signedUrl',
  'signed_url',
  'thumbnail',
  'thumbnailUrl',
  'thumbnail_url'
] as const

const BLOCKED_RESOURCE_VALUES = new Set([
  '[object object]',
  'object object',
  'object%20object',
  '[object%20object]',
  'undefined',
  'null',
  'nan'
])

const MESSAGE_IMAGE_BUCKET_PREFIX = /^\/?message_images\//
const MESSAGE_IMAGES_BUCKET = 'message_images'
const GENERATED_IMAGE_BUCKET_PREFIX = /^\/?generated_images\//
const GENERATED_IMAGES_BUCKET = 'generated_images'

// --------------

function getComparableResourceValue(value: string) {
  const trimmed = value.trim()

  try {
    return decodeURIComponent(trimmed).trim().toLowerCase()
  } catch {
    return trimmed.toLowerCase()
  }
}

// --------------

export function isBlockedResourceValue(value: string) {
  const comparable = getComparableResourceValue(value)

  return (
    comparable.length === 0 ||
    BLOCKED_RESOURCE_VALUES.has(comparable) ||
    comparable.includes('[object object]')
  )
}

// --------------

export function readUrlCandidate(value: unknown): string | null {
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return isBlockedResourceValue(trimmed) ? null : trimmed
  }

  if (value instanceof URL) {
    return value.toString()
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const candidate = readUrlCandidate(item)
      if (candidate) {
        return candidate
      }
    }
  }

  if (!value || typeof value !== 'object') {
    return null
  }

  const record = value as Record<string, unknown>

  for (const key of URL_LIKE_KEYS) {
    const candidate = readUrlCandidate(record[key])
    if (candidate) {
      return candidate
    }
  }

  return null
}

// --------------

export function isModelReadableImageUrl(value: string) {
  return /^https?:\/\//i.test(value) || /^data:/i.test(value)
}

// --------------

/**
 * Legacy image URLs from the retired standalone API era. These hosts/paths
 * are dead (api.losi.ai serves a self-signed Traefik default cert, the
 * losi-uploads S3 objects and losi.ai/api/files uploads no longer exist),
 * and passing one to an image provider fails the ENTIRE request — so they
 * must be repaired or dropped, never forwarded.
 *
 * assets.losi.ai currently presents Traefik's default self-signed cert
 * (CERTIFICATE_VERIFY_FAILED at Replicate/Gemini). Prefer rewriting
 * `/media/messages/...` to signed pipeline.losi.one URLs; treat other
 * assets.losi.ai paths as dead until TLS is fixed.
 */
const LEGACY_CHAT_IMAGE_PATH = /\/chat\/image\/([0-9a-f-]{36})\/(\d+)\/?$/i
const ASSETS_MESSAGE_MEDIA_PATH = /^\/media\/messages\/(.+)$/i

export function parseLegacyChatImageReference(
  value: string
): { chatId: string; index: number } | null {
  try {
    const url = new URL(value)
    const match = url.pathname.match(LEGACY_CHAT_IMAGE_PATH)
    if (!match) return null
    const index = Number(match[2])
    return Number.isInteger(index) && index >= 0
      ? { chatId: match[1], index }
      : null
  } catch {
    return null
  }
}

function isAssetsLosiHost(hostname: string) {
  return hostname === 'assets.losi.ai' || hostname === 'www.assets.losi.ai'
}

/** Map `https://assets.losi.ai/media/messages/{chatId}/{file}` → storage path. */
export function getAssetsCdnMessageImageStoragePath(value: string): string | null {
  try {
    const url = new URL(value)
    if (!isAssetsLosiHost(url.hostname.toLowerCase())) {
      return null
    }

    const match = url.pathname.match(ASSETS_MESSAGE_MEDIA_PATH)
    if (!match) {
      return null
    }

    const storagePath = stripMessageImagesBucketPrefix(match[1])
    return isPlausibleStoragePath(storagePath) ? storagePath : null
  } catch {
    return null
  }
}

export function isKnownDeadImageUrl(value: string): boolean {
  try {
    const url = new URL(value)
    const hostname = url.hostname.toLowerCase()
    if (hostname === 'losi-uploads.s3.amazonaws.com') return true
    if (hostname === 'api.losi.ai') return true
    // Never-existed host the model invents for "chat_images" uploads.
    if (hostname === 'storage.losi.one') return true
    // Broken Traefik TLS — rewrite message media when possible; drop the rest.
    if (isAssetsLosiHost(hostname)) {
      return getAssetsCdnMessageImageStoragePath(value) === null
    }
    // HighLevel / msgsndr CDN assets frequently 404 and poison provider fetches.
    if (
      hostname === 'storage.googleapis.com' &&
      url.pathname.startsWith('/msgsndr/')
    ) {
      return true
    }
    if (
      (hostname === 'losi.ai' ||
        hostname === 'www.losi.ai' ||
        hostname === 'losi.one' ||
        hostname === 'www.losi.one') &&
      /^\/(api\/files|api-files)\//.test(url.pathname)
    ) {
      return true
    }
    return false
  } catch {
    return false
  }
}

// --------------

export function getSupabaseStoragePublicOrigin(): string {
  const fromEnv =
    process.env.NEXT_PUBLIC_SUPABASE_STORAGE_URL?.trim() ||
    process.env.NEXT_PUBLIC_SUPABASE_STORAGE_ORIGIN?.trim()

  if (fromEnv) {
    try {
      return new URL(fromEnv).origin
    } catch {
      // fall through to default
    }
  }

  return DEFAULT_SUPABASE_STORAGE_PUBLIC_ORIGIN
}

/**
 * Rewrite storage object URLs onto the live public storage origin.
 * Legacy `*.supabase.co` public URLs 402; `auth.losi.one` does not serve objects.
 */
export function rewriteSupabaseStorageUrl(value: string): string {
  const trimmed = value.trim()
  if (!trimmed || !/^https?:\/\//i.test(trimmed)) {
    return value
  }

  try {
    const parsed = new URL(trimmed)
    if (!parsed.pathname.includes(STORAGE_OBJECT_PATH)) {
      return value
    }

    const targetOrigin = getSupabaseStoragePublicOrigin()
    if (parsed.origin === targetOrigin) {
      return value
    }

    return `${targetOrigin}${parsed.pathname}${parsed.search}${parsed.hash}`
  } catch {
    return value
  }
}

// --------------

export function isRenderableImageUrl(value: string) {
  return (
    isModelReadableImageUrl(value) ||
    /^blob:/i.test(value) ||
    value.startsWith('/') ||
    value.startsWith('./') ||
    value.startsWith('../')
  )
}

// --------------

export function getSafeRenderableImageUrl(value: unknown): string | undefined {
  const candidate = readUrlCandidate(value)

  if (!candidate || !isRenderableImageUrl(candidate)) {
    return undefined
  }

  return rewriteSupabaseStorageUrl(candidate)
}

// --------------

export function isSafeMarkdownUrl(value: string) {
  if (isBlockedResourceValue(value)) {
    return false
  }

  return (
    /^https?:\/\//i.test(value) ||
    /^mailto:/i.test(value) ||
    value.startsWith('#') ||
    value.startsWith('/') ||
    value.startsWith('./') ||
    value.startsWith('../') ||
    value.startsWith('space-item:') ||
    value.startsWith('connect:') ||
    value.startsWith('space-member:') ||
    value.startsWith('lead:') ||
    value.startsWith('contact:') ||
    value.startsWith('company:') ||
    // Chat mention schemes (@[Name](member:…), (assistant:…), …) —
    // must stay renderable or mentions degrade to "Name [blocked]".
    value.startsWith('member:') ||
    value.startsWith('assistant:') ||
    value.startsWith('everyone:') ||
    value.startsWith('losi:') ||
    value.startsWith('staffing_agent:') ||
    value.startsWith('staffing-agent:') ||
    value.startsWith('super-agent:') ||
    value.startsWith('space_member:')
  )
}

// --------------

export function getSafeMarkdownUrl(value: unknown): string {
  const candidate = readUrlCandidate(value)

  if (!candidate || !isSafeMarkdownUrl(candidate)) {
    return ''
  }

  return candidate
}

// --------------

export function getImageServeStoragePath(value: string): string | null {
  try {
    const url = new URL(value, 'https://losi.local')
    const path = url.searchParams.get('path')?.trim() || null

    return url.pathname === '/api/image/serve' && path ? path : null
  } catch {
    return null
  }
}

// --------------

function decodePath(value: string) {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

// --------------

function stripMessageImagesBucketPrefix(value: string) {
  return decodePath(value)
    .replace(MESSAGE_IMAGE_BUCKET_PREFIX, '')
    .replace(/^\/+/, '')
}

// --------------

function stripGeneratedImagesBucketPrefix(value: string) {
  return decodePath(value)
    .replace(GENERATED_IMAGE_BUCKET_PREFIX, '')
    .replace(/^\/+/, '')
}

// --------------

export function getSupabaseMessageImageStoragePath(value: string): string | null {
  try {
    const url = new URL(value, 'https://losi.local')
    const match = url.pathname.match(
      /\/storage\/v1\/object\/(?:sign|public)\/([^/]+)\/(.+)$/
    )

    if (!match) {
      return null
    }

    const [, bucket, path] = match
    return bucket === MESSAGE_IMAGES_BUCKET ? stripMessageImagesBucketPrefix(path) : null
  } catch {
    return null
  }
}

// --------------

export function getSupabaseGeneratedImageStoragePath(value: string): string | null {
  try {
    const url = new URL(value, 'https://losi.local')
    const match = url.pathname.match(
      /\/storage\/v1\/object\/(?:sign|public)\/([^/]+)\/(.+)$/
    )

    if (!match) {
      return null
    }

    const [, bucket, path] = match
    return bucket === GENERATED_IMAGES_BUCKET ? stripGeneratedImagesBucketPrefix(path) : null
  } catch {
    return null
  }
}

// --------------

function isPlausibleStoragePath(value: string) {
  return (
    value.includes('/') &&
    !value.includes(' ') &&
    !isBlockedResourceValue(value)
  )
}

// --------------

function isPlausibleGeneratedImagePath(value: string) {
  return (
    !value.includes(' ') &&
    !isBlockedResourceValue(value) &&
    // Include HEIC/HEIF — iOS camera rolls still upload these occasionally.
    /\.(png|jpe?g|webp|gif|heic|heif)$/i.test(value)
  )
}

// --------------

export function normalizeMessageImageStorageSource(value: unknown): string | null {
  const candidate = readUrlCandidate(value)

  if (!candidate) {
    return null
  }

  const servedPath = getImageServeStoragePath(candidate)
  if (servedPath) {
    const storagePath = stripMessageImagesBucketPrefix(servedPath)
    return isPlausibleStoragePath(storagePath) ? storagePath : null
  }

  const supabaseStoragePath = getSupabaseMessageImageStoragePath(candidate)
  if (supabaseStoragePath) {
    return isPlausibleStoragePath(supabaseStoragePath) ? supabaseStoragePath : null
  }

  // assets.losi.ai/media/messages/{chatId}/{file} mirrors message_images but
  // currently serves Traefik's self-signed cert — rewrite to a storage path
  // so callers sign via pipeline.losi.one instead of forwarding the broken host.
  const assetsCdnPath = getAssetsCdnMessageImageStoragePath(candidate)
  if (assetsCdnPath) {
    return assetsCdnPath
  }

  if (
    isModelReadableImageUrl(candidate) ||
    /^blob:/i.test(candidate)
  ) {
    return candidate
  }

  const storagePath = stripMessageImagesBucketPrefix(candidate)

  return isPlausibleStoragePath(storagePath) ? storagePath : null
}

// --------------

export function getGeneratedImageStoragePath(value: unknown): string | null {
  const candidate = readUrlCandidate(value)

  if (!candidate) {
    return null
  }

  const servedPath = getImageServeStoragePath(candidate)
  if (servedPath) {
    if (!GENERATED_IMAGE_BUCKET_PREFIX.test(servedPath)) {
      return null
    }

    const storagePath = stripGeneratedImagesBucketPrefix(servedPath)
    return isPlausibleGeneratedImagePath(storagePath) ? storagePath : null
  }

  const supabaseStoragePath = getSupabaseGeneratedImageStoragePath(candidate)
  if (supabaseStoragePath) {
    return isPlausibleGeneratedImagePath(supabaseStoragePath)
      ? supabaseStoragePath
      : null
  }

  if (
    isModelReadableImageUrl(candidate) ||
    /^blob:/i.test(candidate) ||
    candidate.startsWith('/')
  ) {
    return null
  }

  const isGeneratedBucketPath = GENERATED_IMAGE_BUCKET_PREFIX.test(candidate)
  const storagePath = stripGeneratedImagesBucketPrefix(candidate)
  if (!isGeneratedBucketPath && storagePath.includes('/')) {
    return null
  }

  return isPlausibleGeneratedImagePath(storagePath) ? storagePath : null
}

// --------------

export function getMessageImageStoragePath(value: unknown): string | null {
  const source = normalizeMessageImageStorageSource(value)

  if (
    !source ||
    isModelReadableImageUrl(source) ||
    /^blob:/i.test(source) ||
    isRenderableImageUrl(source)
  ) {
    return null
  }

  return source
}
